// The right panel's home, as ONE pure projection (right-panel coherence v2,
// .claude/analysis/right-panel-coherence-2026-10-06/README.md).
//
// A `ChatWorld` is everything one chat touched, built from data the client
// already holds: the resources the conversation involves (`conversationResources`,
// with HOW each came in), each of its materials' quality read (the per-claim
// citations: claim + quote + source + page), and the citations under the chat's
// own replies. `homeOf` sorts it into the panel's three groups; every boundary
// rule lives here, never in the view:
//
//   - nothing at all            → one empty state, no empty headings;
//   - materials made HERE       → group 1 (always first, even when empty);
//   - a part of a material made here (planned here, or only read/mentioned)
//                               → under that material in group 1, never on
//                               its own and never "from another chat";
//   - a source ATTACHED or CITED (by a material here or a chat reply) → group 2,
//     most-used first; attached but unused last ("not used yet");
//   - a source the agent only READ and nothing cites → group 3;
//   - a material from another chat (@-mentioned / read), a package, a file
//                               → group 3, always.
//
//   - a source the chat's goal answers to (its requirement document) → the
//                               "Mål" group only (it is not cited, so it
//                               would read "not used yet" forever here).
//   - a source a goal DRAWS ON (D56) → group 2 like an attached one, once,
//                               marked "via målet …" (whether or not the
//                               chat also has it).
//
// A source only a chat reply cites counts as used (owner default 2026-10-06).
//
// Pure: total folds over values. No I/O, no casts.

import type {
    ChatCitation,
    ChatEntry,
} from "../../../components/Chat/types.js";
import type {
    ExportRef,
    FileRef,
    MaterialRef,
    ResourceRef,
    SourceRef,
} from "../../../components/Navigation/types.js";
import {
    type MaterialQuality,
    type StatementSource,
    statementSourcesOf,
} from "../../../components/QualitySummary/QualitySummary.js";
import type { InvolvedResource } from "../conversationResources.js";

// ── The world ────────────────────────────────────────────────────────────────

// A material made in this chat, with its quality read (null until it lands, or
// when the read failed: the views then show "not checked", never a guess).
// `parts` are its parts the chat involves (a course's modules), listed under it
// and never as materials of their own (bd-lya); [] for none.
export type ChatMaterial = {
    readonly ref: MaterialRef;
    readonly quality: MaterialQuality | null;
    readonly parts: readonly MaterialRef[];
};

// How a source came into the chat. `cited` = it is in the chat only because a
// material made here cites it.
// `goal` = it is in the chat only because one of its goals draws on it.
type SourceArrival = "attached" | "read" | "cited" | "goal";

export type ChatSource = {
    readonly ref: SourceRef;
    readonly arrival: SourceArrival;
    // The goal (its shown name) that brings the source along, if any.
    readonly viaGoal?: string;
};

// A source a goal draws on, named by that goal.
export type GoalSourceLink = {
    readonly sourceId: string;
    readonly title: string;
    readonly goalTitle: string;
};

// One citation under one of the chat's replies, with the reply it sits in (so
// "cited in N replies" can jump to them).
type ReplyCitation = {
    readonly entryId: string;
    readonly citation: ChatCitation;
};

// Everything else the chat touched.
export type OtherFile =
    | { readonly type: "material-elsewhere"; readonly ref: MaterialRef }
    | { readonly type: "read-uncited"; readonly ref: SourceRef }
    | { readonly type: "package"; readonly ref: ExportRef }
    | { readonly type: "file"; readonly ref: FileRef };

export type ChatWorld = {
    readonly materials: readonly ChatMaterial[];
    readonly sources: readonly ChatSource[];
    readonly replies: readonly ReplyCitation[];
    readonly elsewhere: readonly OtherFile[];
};

export const emptyWorld: ChatWorld = {
    materials: [],
    sources: [],
    replies: [],
    elsewhere: [],
};

const repliesOf = (entries: readonly ChatEntry[]): readonly ReplyCitation[] =>
    entries.flatMap((entry) =>
        entry.type === "agent" && entry.citations !== undefined
            ? entry.citations.map((citation) => ({
                  entryId: entry.id,
                  citation,
              }))
            : [],
    );

// A resource that is neither a material made here (or a part of one) nor a
// source.
const otherOf =
    (isPartHere: (r: InvolvedResource) => boolean) =>
    (r: InvolvedResource): readonly OtherFile[] => {
        const { ref, involvement } = r;
        switch (ref.type) {
            case "material":
                return involvement === "created" || isPartHere(r)
                    ? []
                    : [{ type: "material-elsewhere", ref }];
            case "export":
                return [{ type: "package", ref }];
            case "file":
                return [{ type: "file", ref }];
            case "source":
                return [];
        }
    };

// Build the world from what the conversation involves. `qualityOf` is the
// loaded quality per material id; `resolve` is the library index (fresh titles;
// a cited source the library no longer holds keeps the citation's title).
export const worldOf = (input: {
    readonly involved: readonly InvolvedResource[];
    readonly entries: readonly ChatEntry[];
    readonly qualityOf: (materialId: string) => MaterialQuality | null;
    readonly resolve: (id: string) => ResourceRef | undefined;
}): ChatWorld => {
    const made = new Set(
        input.involved
            .filter(
                (r) => r.ref.type === "material" && r.involvement === "created",
            )
            .map((r) => r.ref.id),
    );
    const isPartHere = (r: InvolvedResource): boolean =>
        r.partOf !== undefined && r.partOf !== r.ref.id && made.has(r.partOf);
    // The top material a part sits under (a part of a part climbs on).
    const parent = new Map(
        input.involved.flatMap((r): readonly [string, string][] =>
            isPartHere(r) && r.partOf !== undefined
                ? [[r.ref.id, r.partOf]]
                : [],
        ),
    );
    const topOf = (id: string, seen: ReadonlySet<string>): string => {
        const up = parent.get(id);
        return up === undefined || seen.has(up)
            ? id
            : topOf(up, new Set([...seen, id]));
    };
    const partsOf = (rootId: string): readonly MaterialRef[] =>
        input.involved.flatMap((r) =>
            r.ref.type === "material" &&
            isPartHere(r) &&
            topOf(r.ref.id, new Set()) === rootId
                ? [r.ref]
                : [],
        );
    const materials = input.involved.flatMap((r): readonly ChatMaterial[] =>
        r.ref.type === "material" &&
        r.involvement === "created" &&
        !isPartHere(r)
            ? [
                  {
                      ref: r.ref,
                      quality: input.qualityOf(r.ref.id),
                      parts: partsOf(r.ref.id),
                  },
              ]
            : [],
    );
    const involvedSources = input.involved.flatMap(
        ({ ref, involvement }): readonly ChatSource[] =>
            ref.type === "source"
                ? [
                      {
                          ref,
                          arrival:
                              involvement === "attached" ? "attached" : "read",
                      },
                  ]
                : [],
    );
    const known = new Set(involvedSources.map((s) => s.ref.id));
    const citedOnly = materials
        .flatMap((m) => statementSourcesOf(m.quality))
        .filter((row) => !known.has(row.source.id))
        .filter(
            (row, i, rows) =>
                rows.findIndex((r) => r.source.id === row.source.id) === i,
        )
        .map((row): ChatSource => {
            const ref = input.resolve(row.source.id);
            return {
                ref:
                    ref?.type === "source"
                        ? ref
                        : {
                              type: "source",
                              id: row.source.id,
                              title: row.source.title,
                          },
                arrival: "cited",
            };
        });
    const elsewhere = input.involved.flatMap(otherOf(isPartHere));
    return {
        materials,
        sources: [...involvedSources, ...citedOnly],
        replies: repliesOf(input.entries),
        elsewhere,
    };
};

// ── Usage: where one source is cited ────────────────────────────────────────

export type MaterialUse = {
    readonly material: MaterialRef;
    readonly rows: readonly StatementSource[];
};

export type SourceUsage = {
    readonly source: ChatSource;
    readonly inMaterials: readonly MaterialUse[];
    readonly inChat: readonly ReplyCitation[];
};

export const usesOf = (u: SourceUsage): number =>
    u.inMaterials.reduce((n, m) => n + m.rows.length, 0) + u.inChat.length;

export const isUnused = (u: SourceUsage): boolean => usesOf(u) === 0;

// The distinct replies a source is cited in (a reply citing it twice counts once).
export const replyIdsOf = (u: SourceUsage): readonly string[] =>
    u.inChat
        .map((r) => r.entryId)
        .filter((id, i, ids) => ids.indexOf(id) === i);

export const usageOf = (world: ChatWorld, source: ChatSource): SourceUsage => ({
    source,
    inMaterials: world.materials
        .map(
            (m): MaterialUse => ({
                material: m.ref,
                rows: statementSourcesOf(m.quality).filter(
                    (r) => r.source.id === source.ref.id,
                ),
            }),
        )
        .filter((use) => use.rows.length > 0),
    inChat: world.replies.filter(
        (r) => r.citation.resourceId === source.ref.id,
    ),
});

// ── The three groups ────────────────────────────────────────────────────────

export type PanelHome =
    | { readonly type: "empty" }
    | {
          readonly type: "populated";
          readonly materials: readonly ChatMaterial[];
          readonly sources: readonly SourceUsage[];
          readonly others: readonly OtherFile[];
      };

// The chat's sources plus its goals' sources, each once: a goal source the
// chat already has keeps how it came in and gains the goal's mark.
const withGoalSources = (
    sources: readonly ChatSource[],
    goalSources: readonly GoalSourceLink[],
): readonly ChatSource[] => {
    const via = new Map(goalSources.map((g) => [g.sourceId, g.goalTitle]));
    const known = new Set(sources.map((s) => s.ref.id));
    return [
        ...sources.map((s): ChatSource => {
            const goal = via.get(s.ref.id);
            return goal === undefined ? s : { ...s, viaGoal: goal };
        }),
        ...goalSources
            .filter((g) => !known.has(g.sourceId))
            .map(
                (g): ChatSource => ({
                    ref: { type: "source", id: g.sourceId, title: g.title },
                    arrival: "goal",
                    viaGoal: g.goalTitle,
                }),
            ),
    ];
};

export const homeOf = (
    world: ChatWorld,
    requirementIds: ReadonlySet<string> = new Set(),
    goalSources: readonly GoalSourceLink[] = [],
): PanelHome => {
    const usages = withGoalSources(world.sources, goalSources)
        .filter((s) => !requirementIds.has(s.ref.id))
        .map((s) => usageOf(world, s));
    const inGroupTwo = (u: SourceUsage): boolean =>
        u.source.arrival === "attached" ||
        u.source.viaGoal !== undefined ||
        !isUnused(u);
    // `filter` returns a fresh array; the sort is stable, so ties keep the
    // order the chat met them in.
    const sources = usages
        .filter(inGroupTwo)
        .sort((a, b) => usesOf(b) - usesOf(a));
    const others: readonly OtherFile[] = [
        ...world.elsewhere,
        ...usages
            .filter((u) => !inGroupTwo(u))
            .map(
                (u): OtherFile => ({ type: "read-uncited", ref: u.source.ref }),
            ),
    ];
    return world.materials.length + sources.length + others.length === 0
        ? { type: "empty" }
        : { type: "populated", materials: world.materials, sources, others };
};

// Every ref the chat touched: the header's teleport roster.
export const refsOf = (world: ChatWorld): readonly ResourceRef[] => [
    ...world.materials.flatMap((m) => [m.ref, ...m.parts]),
    ...world.sources.map((s) => s.ref),
    ...world.elsewhere.map((o) => o.ref),
];
